import { useParams } from "@tanstack/solid-router";
import { For, Show, createMemo, createSignal } from "solid-js";

import { AppShell } from "~/components/AppShell";
import {
  useDealsByOwner,
  useLeadScoreDistribution,
  usePipelineByStage,
  usePipelines,
} from "~/lib/hooks";
import type { DealsByOwnerRow, LeadScoreBand, PipelineByStageRow } from "~/lib/types";

type ReportKey = "pipeline_by_stage" | "deals_by_owner" | "lead_score_distribution";

const REPORTS: { key: ReportKey; label: string; description: string }[] = [
  {
    key: "pipeline_by_stage",
    label: "Pipeline by stage",
    description: "Deal count + value at each pipeline stage. Stacked bars split open vs won.",
  },
  {
    key: "deals_by_owner",
    label: "Deals by owner",
    description: "Per-rep open/won/lost counts + open + won amount.",
  },
  {
    key: "lead_score_distribution",
    label: "Lead score distribution",
    description: "Histogram of contacts across hot/warm/cool/cold/unscored bands.",
  },
];

export function ReportsPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string };
  const [selected, setSelected] = createSignal<ReportKey>("pipeline_by_stage");

  return (
    <AppShell wsKey={params().wsKey} pageTitle="Reports">
      <div class="grid gap-4 md:grid-cols-[16rem_1fr]">
        {/* Sidebar */}
        <nav class="card bg-base-100 shadow-sm h-fit" aria-label="Available reports">
          <div class="card-body p-2">
            <ul class="menu menu-sm">
              <For each={REPORTS}>
                {(r) => (
                  <li>
                    <button
                      type="button"
                      class={selected() === r.key ? "active" : ""}
                      onClick={() => setSelected(r.key)}
                    >
                      {r.label}
                    </button>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </nav>

        {/* Main */}
        <div class="flex flex-col gap-4">
          <Show when={selected() === "pipeline_by_stage"}>
            <PipelineByStageReportView wsKey={() => params().wsKey} />
          </Show>
          <Show when={selected() === "deals_by_owner"}>
            <DealsByOwnerReportView wsKey={() => params().wsKey} />
          </Show>
          <Show when={selected() === "lead_score_distribution"}>
            <LeadScoreReportView wsKey={() => params().wsKey} />
          </Show>
        </div>
      </div>
    </AppShell>
  );
}

// ── Pipeline by stage ────────────────────────────────────────────────────────

function PipelineByStageReportView(props: { wsKey: () => string }) {
  const pipelines = usePipelines(props.wsKey);
  const [pipelineId, setPipelineId] = createSignal<string | undefined>(undefined);

  // Default to the workspace's default pipeline once they load.
  const effectivePipelineId = createMemo(() => {
    const chosen = pipelineId();
    if (chosen) return chosen;
    return pipelines.data?.find((p) => p.is_default)?.id ?? pipelines.data?.[0]?.id;
  });

  const report = usePipelineByStage(props.wsKey, effectivePipelineId);

  // Max stage total — drives bar widths.
  const maxTotal = createMemo(() => {
    const rows = report.data?.rows ?? [];
    return Math.max(1, ...rows.map((r) => r.open_amount + r.won_amount));
  });

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h2 class="card-title text-lg">Pipeline by stage</h2>
            <p class="text-xs opacity-60">Stacked bars show open (lighter) vs won (darker).</p>
          </div>
          <div class="flex gap-2 items-center">
            <Show when={(pipelines.data?.length ?? 0) > 1}>
              <select
                class="select select-bordered select-sm"
                value={effectivePipelineId() ?? ""}
                onChange={(e) => setPipelineId(e.currentTarget.value)}
                aria-label="Pipeline"
              >
                <For each={pipelines.data ?? []}>
                  {(p) => <option value={p.id}>{p.name}</option>}
                </For>
              </select>
            </Show>
            <DownloadCsvButton
              disabled={!report.data}
              filename={`pipeline-by-stage.csv`}
              rows={() => (report.data?.rows ?? []).map(pipelineRowToCsv)}
            />
          </div>
        </div>

        <Show when={report.isLoading}>
          <p class="text-sm opacity-60 mt-3">Loading…</p>
        </Show>

        <Show when={report.data}>
          {(r) => (
            <div class="mt-4">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Stage</th>
                    <th class="text-right">Deals</th>
                    <th class="text-right">Open</th>
                    <th class="text-right">Won</th>
                    <th>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={r().rows}>
                    {(row) => (
                      <tr>
                        <td>
                          <div class="font-medium">{row.stage_name}</div>
                          <div class="text-xs opacity-60">{row.stage_type}</div>
                        </td>
                        <td class="text-right">{row.deal_count}</td>
                        <td class="text-right">{formatMoney(row.open_amount)}</td>
                        <td class="text-right">{formatMoney(row.won_amount)}</td>
                        <td>
                          <StackedBar
                            open={row.open_amount}
                            won={row.won_amount}
                            max={maxTotal()}
                          />
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
}

function StackedBar(props: { open: number; won: number; max: number }) {
  const openPct = () => (props.open / props.max) * 100;
  const wonPct = () => (props.won / props.max) * 100;
  return (
    <div
      class="h-3 bg-base-200 rounded overflow-hidden flex w-full min-w-[8rem]"
      title={`open ${props.open.toLocaleString()} · won ${props.won.toLocaleString()}`}
    >
      <div class="bg-primary/40 h-full" style={{ width: `${openPct()}%` }} />
      <div class="bg-primary h-full" style={{ width: `${wonPct()}%` }} />
    </div>
  );
}

// ── Deals by owner ───────────────────────────────────────────────────────────

function DealsByOwnerReportView(props: { wsKey: () => string }) {
  const report = useDealsByOwner(props.wsKey);

  const maxAmount = createMemo(() => {
    const rows = report.data?.rows ?? [];
    return Math.max(1, ...rows.map((r) => r.open_amount + r.won_amount));
  });

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h2 class="card-title text-lg">Deals by owner</h2>
            <p class="text-xs opacity-60">Open + won amount per rep. Bars share a scale.</p>
          </div>
          <DownloadCsvButton
            disabled={!report.data}
            filename="deals-by-owner.csv"
            rows={() => (report.data?.rows ?? []).map(ownerRowToCsv)}
          />
        </div>

        <Show when={report.isLoading}>
          <p class="text-sm opacity-60 mt-3">Loading…</p>
        </Show>

        <Show when={report.data}>
          {(r) => (
            <div class="mt-4 overflow-x-auto">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>Owner</th>
                    <th class="text-right">Open</th>
                    <th class="text-right">Won</th>
                    <th class="text-right">Lost</th>
                    <th class="text-right">Open value</th>
                    <th class="text-right">Won value</th>
                    <th>Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  <For each={r().rows}>
                    {(row) => (
                      <tr>
                        <td class="font-medium">{row.owner_label}</td>
                        <td class="text-right">{row.open_count}</td>
                        <td class="text-right">{row.won_count}</td>
                        <td class="text-right">{row.lost_count}</td>
                        <td class="text-right">{formatMoney(row.open_amount)}</td>
                        <td class="text-right">{formatMoney(row.won_amount)}</td>
                        <td>
                          <StackedBar
                            open={row.open_amount}
                            won={row.won_amount}
                            max={maxAmount()}
                          />
                        </td>
                      </tr>
                    )}
                  </For>
                </tbody>
              </table>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
}

// ── Lead score distribution ──────────────────────────────────────────────────

function LeadScoreReportView(props: { wsKey: () => string }) {
  const report = useLeadScoreDistribution(props.wsKey);

  const maxCount = createMemo(() => {
    const bands = report.data?.bands ?? [];
    return Math.max(1, ...bands.map((b) => b.count));
  });

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between gap-3">
          <div>
            <h2 class="card-title text-lg">Lead score distribution</h2>
            <p class="text-xs opacity-60">
              <Show when={report.data}>{report.data!.total_contacts} active contacts ·</Show> bands
              match the contact-list filter chips.
            </p>
          </div>
          <DownloadCsvButton
            disabled={!report.data}
            filename="lead-score-distribution.csv"
            rows={() => (report.data?.bands ?? []).map(bandToCsv)}
          />
        </div>

        <Show when={report.isLoading}>
          <p class="text-sm opacity-60 mt-3">Loading…</p>
        </Show>

        <Show when={report.data}>
          {(r) => (
            <div class="mt-4">
              <ul class="flex flex-col gap-2">
                <For each={r().bands}>
                  {(b) => (
                    <li class="grid grid-cols-[10rem_1fr_4rem] gap-3 items-center">
                      <span class="text-sm font-medium truncate">{b.label}</span>
                      <div
                        class="h-4 bg-base-200 rounded overflow-hidden"
                        title={`${b.count} contacts`}
                      >
                        <div
                          class={`h-full ${bandClass(b.band)}`}
                          style={{ width: `${(b.count / maxCount()) * 100}%` }}
                        />
                      </div>
                      <span class="text-sm text-right font-mono">{b.count}</span>
                    </li>
                  )}
                </For>
              </ul>
            </div>
          )}
        </Show>
      </div>
    </div>
  );
}

function bandClass(band: string): string {
  switch (band) {
    case "hot":
      return "bg-success";
    case "warm":
      return "bg-warning";
    case "cool":
      return "bg-info";
    case "cold":
      return "bg-base-content/30";
    default:
      return "bg-base-content/15";
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatMoney(amount: number): string {
  if (!amount) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return amount.toLocaleString();
  }
}

function pipelineRowToCsv(r: PipelineByStageRow): Record<string, string | number> {
  return {
    stage: r.stage_name,
    stage_type: r.stage_type,
    deal_count: r.deal_count,
    open_amount: r.open_amount,
    won_amount: r.won_amount,
    total_amount: r.total_amount,
  };
}

function ownerRowToCsv(r: DealsByOwnerRow): Record<string, string | number> {
  return {
    owner: r.owner_label,
    open_count: r.open_count,
    won_count: r.won_count,
    lost_count: r.lost_count,
    open_amount: r.open_amount,
    won_amount: r.won_amount,
  };
}

function bandToCsv(b: LeadScoreBand): Record<string, string | number> {
  return {
    band: b.band,
    label: b.label,
    min_score: b.min_score ?? "",
    max_score: b.max_score ?? "",
    count: b.count,
  };
}

function DownloadCsvButton(props: {
  filename: string;
  rows: () => Record<string, string | number>[];
  disabled?: boolean;
}) {
  const handle = () => {
    const rows = props.rows();
    if (rows.length === 0) return;
    const headers = Object.keys(rows[0]);
    const escape = (v: string | number) => {
      const s = String(v ?? "");
      if (s.includes(",") || s.includes('"') || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [
      headers.join(","),
      ...rows.map((r) => headers.map((h) => escape(r[h])).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = props.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <button type="button" class="btn btn-sm btn-ghost" onClick={handle} disabled={props.disabled}>
      Download CSV
    </button>
  );
}
